import { Capacitor } from '@capacitor/core';
import {
  LOG_LEVEL,
  PURCHASES_ERROR_CODE,
  Purchases,
  type CustomerInfo,
  type PurchasesPackage,
} from '@revenuecat/purchases-capacitor';
import { httpsCallable } from 'firebase/functions';
import { auth, ensureAppCheckReady, functions } from '../firebase';
import {
  PLUS_ANNUAL_PRODUCT_ID,
  PLUS_MONTHLY_PRODUCT_ID,
  REVENUECAT_PLUS_ENTITLEMENT_ID,
  plusPeriodForProduct,
  revenueCatWebPurchaseUrl,
  type PlusPeriod,
  type PlusPriceOption,
} from '../domain/monetization';

export interface NativePlusOption extends PlusPriceOption {
  nativePackage: PurchasesPackage;
}

export interface PurchaseResult {
  active: boolean;
  cancelled: boolean;
  managementUrl?: string;
  /** The store operation finished, but the server has not confirmed access. */
  syncPending?: boolean;
}

const plusEntitlementId = import.meta.env.VITE_REVENUECAT_PLUS_ENTITLEMENT_ID?.trim() || REVENUECAT_PLUS_ENTITLEMENT_ID;
const monthlyProductId = import.meta.env.VITE_REVENUECAT_PLUS_MONTHLY_PRODUCT_ID?.trim() || PLUS_MONTHLY_PRODUCT_ID;
const annualProductId = import.meta.env.VITE_REVENUECAT_PLUS_ANNUAL_PRODUCT_ID?.trim() || PLUS_ANNUAL_PRODUCT_ID;

function nativeApiKey() {
  const platform = Capacitor.getPlatform();
  if (platform === 'ios') return import.meta.env.VITE_REVENUECAT_IOS_API_KEY?.trim() || '';
  if (platform === 'android') return import.meta.env.VITE_REVENUECAT_ANDROID_API_KEY?.trim() || '';
  return '';
}

export function nativePurchasesConfigured() {
  return Capacitor.isNativePlatform() && Boolean(nativeApiKey());
}

async function ensureNativePurchases(appUserId: string) {
  requirePurchaseAccount(appUserId);
  const apiKey = nativeApiKey();
  if (!Capacitor.isNativePlatform() || !apiKey) throw new Error('PURCHASES_NOT_CONFIGURED');

  const { isConfigured } = await Purchases.isConfigured();
  requirePurchaseAccount(appUserId);
  if (!isConfigured) {
    await Purchases.configure({ apiKey, appUserID: appUserId });
    if (import.meta.env.DEV) await Purchases.setLogLevel({ level: LOG_LEVEL.DEBUG });
    requirePurchaseAccount(appUserId);
    return;
  }

  const { appUserID } = await Purchases.getAppUserID();
  requirePurchaseAccount(appUserId);
  if (appUserID !== appUserId) await Purchases.logIn({ appUserID: appUserId });
  requirePurchaseAccount(appUserId);
}

function requirePurchaseAccount(appUserId: string) {
  if (auth.currentUser?.uid !== appUserId) throw new Error('PURCHASE_ACCOUNT_CHANGED');
}

function resultFromCustomerInfo(customerInfo: CustomerInfo): PurchaseResult {
  return {
    active: Boolean(customerInfo.entitlements.active[plusEntitlementId]?.isActive),
    cancelled: false,
    managementUrl: customerInfo.managementURL ?? undefined,
  };
}

export async function loadNativePlusOptions(appUserId: string): Promise<{ options: NativePlusOption[]; managementUrl?: string }> {
  await ensureNativePurchases(appUserId);
  const [{ current }, { customerInfo }] = await Promise.all([
    Purchases.getOfferings(),
    Purchases.getCustomerInfo(),
  ]);
  requirePurchaseAccount(appUserId);
  const options = (current?.availablePackages ?? []).flatMap((aPackage): NativePlusOption[] => {
    const period = plusPeriodForProduct(aPackage.product.identifier, monthlyProductId, annualProductId);
    if (!period) return [];
    return [{
      id: aPackage.identifier,
      productId: aPackage.product.identifier,
      period,
      priceLabel: aPackage.product.priceString,
      detailLabel: period === 'annual' ? aPackage.product.pricePerMonthString ?? undefined : undefined,
      nativePackage: aPackage,
    }];
  });
  options.sort((left, right) => left.period === 'annual' && right.period !== 'annual' ? -1 : 1);
  return { options, managementUrl: customerInfo.managementURL ?? undefined };
}

export function loadWebPlusOptions(appUserId: string): PlusPriceOption[] {
  const purchaseLink = import.meta.env.VITE_REVENUECAT_WEB_PURCHASE_URL?.trim() || '';
  if (!purchaseLink) return [];
  const monthlyPackage = import.meta.env.VITE_REVENUECAT_WEB_MONTHLY_PACKAGE_ID?.trim() || '$rc_monthly';
  const annualPackage = import.meta.env.VITE_REVENUECAT_WEB_ANNUAL_PACKAGE_ID?.trim() || '$rc_annual';
  const candidates: Array<PlusPriceOption & { packageId: string }> = [
    {
      id: monthlyPackage,
      packageId: monthlyPackage,
      productId: monthlyProductId,
      period: 'monthly',
      priceLabel: import.meta.env.VITE_REVENUECAT_WEB_MONTHLY_PRICE?.trim() || '',
    },
    {
      id: annualPackage,
      packageId: annualPackage,
      productId: annualProductId,
      period: 'annual',
      priceLabel: import.meta.env.VITE_REVENUECAT_WEB_ANNUAL_PRICE?.trim() || '',
    },
  ];
  return candidates.filter(({ packageId }) => Boolean(revenueCatWebPurchaseUrl(purchaseLink, appUserId, packageId)));
}

export function webCheckoutUrl(appUserId: string, option: PlusPriceOption) {
  const purchaseLink = import.meta.env.VITE_REVENUECAT_WEB_PURCHASE_URL?.trim() || '';
  return revenueCatWebPurchaseUrl(purchaseLink, appUserId, option.id);
}

export async function purchaseNativePlus(appUserId: string, option: NativePlusOption): Promise<PurchaseResult> {
  await ensureNativePurchases(appUserId);
  try {
    const { customerInfo } = await Purchases.purchasePackage({ aPackage: option.nativePackage });
    return reconcileStoreResult(appUserId, customerInfo);
  } catch (error) {
    const purchaseError = error as { code?: string | number; userCancelled?: boolean | null };
    if (purchaseError.userCancelled || purchaseError.code === PURCHASES_ERROR_CODE.PURCHASE_CANCELLED_ERROR) {
      return { active: false, cancelled: true };
    }
    throw error;
  }
}

export async function restoreNativePurchases(appUserId: string): Promise<PurchaseResult> {
  await ensureNativePurchases(appUserId);
  const { customerInfo } = await Purchases.restorePurchases();
  return reconcileStoreResult(appUserId, customerInfo);
}

async function reconcileStoreResult(appUserId: string, customerInfo: CustomerInfo): Promise<PurchaseResult> {
  const result = resultFromCustomerInfo(customerInfo);
  try {
    const confirmed = await syncServerEntitlement(appUserId);
    return { ...result, active: confirmed.active, syncPending: false };
  } catch {
    // A completed store transaction must not become a generic purchase failure.
    return { ...result, syncPending: true };
  }
}

export async function syncServerEntitlement(expectedUserId?: string) {
  if (expectedUserId) requirePurchaseAccount(expectedUserId);
  await ensureAppCheckReady();
  if (expectedUserId) requirePurchaseAccount(expectedUserId);
  const call = httpsCallable<Record<string, never>, { active: boolean; tier: 'free' | 'plus' }>(functions, 'syncRevenueCatEntitlement');
  const result = (await call({})).data;
  if (expectedUserId) requirePurchaseAccount(expectedUserId);
  return result;
}

export async function disconnectPurchasesUser(isCurrent: () => boolean = () => true) {
  if (!isCurrent()) return;
  if (!Capacitor.isNativePlatform()) return;
  const { isConfigured } = await Purchases.isConfigured().catch(() => ({ isConfigured: false }));
  if (isCurrent() && isConfigured) await Purchases.logOut().catch(() => undefined);
}
